/**
 * Poseidon dashboard — command runner.
 *
 * Every §3.1 control goes through this hook. It never sends anything the
 * operator did not explicitly confirm, it never retries a command
 * automatically, and it surfaces the daemon's own rejection reason unchanged.
 */
import { useCallback, useState } from 'react';
import { ApiError } from '../api/client';
import type { CommandKind, CommandReceipt } from '../api/types';
import { useServices } from '../state/Services';

export interface CommandState {
  busy: boolean;
  error: ApiError | null;
  receipt: CommandReceipt | null;
}

export interface UseCommandResult extends CommandState {
  /** Runs the command. Resolves to the receipt, or null when it was rejected. */
  run: (command: CommandKind, payload: unknown, options: { confirmation: string; reason?: string | null }) => Promise<CommandReceipt | null>;
  reset: () => void;
}

export function useCommand(onSuccess?: (receipt: CommandReceipt) => void): UseCommandResult {
  const { sendCommand } = useServices();
  const [state, setState] = useState<CommandState>({ busy: false, error: null, receipt: null });

  const reset = useCallback(() => setState({ busy: false, error: null, receipt: null }), []);

  const run = useCallback(
    async (
      command: CommandKind,
      payload: unknown,
      options: { confirmation: string; reason?: string | null },
    ): Promise<CommandReceipt | null> => {
      setState({ busy: true, error: null, receipt: null });
      try {
        const receipt = await sendCommand(command, payload, options);
        setState({ busy: false, error: null, receipt });
        if (receipt.accepted) onSuccess?.(receipt);
        return receipt;
      } catch (error) {
        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError(0, { code: 'command_failed', message: 'The command could not be delivered.' }, 'command failed');
        setState({ busy: false, error: apiError, receipt: null });
        return null;
      }
    },
    [sendCommand, onSuccess],
  );

  return { ...state, run, reset };
}
